import { GenerateNCFileURL, GetNCFileById } from '@interfaces/methods';
import { Machine } from '@interfaces/machines';
import { NCFileSearchItem, NCFileVersions } from '@interfaces/search';
import { WithColor } from '@styles/colors';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

// Download helpers types

export interface GetNcFileHashAndTransferWithAPICallParams {
  getNCFileById: GetNCFileById;
  ncFileId: number;
}

export interface GetNCFileHashAndTransferParams extends Pick<GetNcFileHashAndTransferWithAPICallParams, 'getNCFileById'> {
  selectedItem?: Machine;
  selectedVersion?: NCFileVersions;
  ncFileId?: number;
}

export interface ShareNCFileParams extends Pick<GetNCFileHashAndTransferParams, 'getNCFileById' | 'selectedItem'> {
  generateNCFileURL: GenerateNCFileURL;
  isDetailsScreen: boolean;
  ncFileData: NCFileSearchItem | null;
  fileName?: string;
}

// NC file syntax token types

export enum NCTokenType {
  DEFAULT = 'default',
  LINE_NUMBER = 'lineNumber',
  VALUE = 'value',
  COMMENT = 'comment',
  FMAX = 'fmax',
}

export interface NCToken {
  value: string;
  type: NCTokenType;
  noTrailingSpace?: boolean;
}

export interface NCParsedLine {
  key: string;
  tokens: NCToken[];
  isToolCall: boolean;
  toolSectionIndex: number | null;
  rawText: string;
}

export enum NCListItemType {
  LINE = 'line',
  TOOL_CALL_HEADER = 'toolCallHeader',
  TOOL_CALL_LINE = 'toolCallLine',
}

export type NCListItem =
  | { type: NCListItemType.LINE; key: string; line: NCParsedLine }
  | { type: NCListItemType.TOOL_CALL_HEADER; key: string; line: NCParsedLine; sectionIndex: number }
  | { type: NCListItemType.TOOL_CALL_LINE; key: string; line: NCParsedLine; sectionIndex: number };

export interface NCFileContentFontConfig {
  fontSize?: number;
  lineHeight?: number;
  fontWeight?: string;
  letterSpacing?: number;
  fontFamily?: string;
}

// Shared NC file viewer prop fragments

export interface NCFileScaleProps {
  scale: ScaleFunction;
}

export interface NCFileStylingProps {
  paddingHorizontal: number;
  ncFileTextColor?: string;
  singleColor?: boolean;
  ncFileContentFontConfig?: NCFileContentFontConfig;
}

// Parser types

export interface ParseNCFileContentOptions {
  withCollapsing: boolean;
}

// Component types

export interface NCTextVirtualizedRef {
  collapseAll: () => void;
  expandAll: () => void;
  scrollToTop: () => void;
}

export interface NCLineProps extends WithColor, NCFileScaleProps, NCFileStylingProps {
  line: NCParsedLine;
}

export interface NCToolCallHeaderProps extends Pick<NCLineProps, 'C' | 'line' | 'paddingHorizontal' | 'scale' | 'ncFileTextColor' | 'singleColor' | 'ncFileContentFontConfig'> {
  isExpanded: boolean;
  onPress: () => void;
}

export interface NCTextVirtualizedProps extends WithColor, NCFileScaleProps, NCFileStylingProps {
  data: string;
  backgroundColor: string;
  initialCollapsed: boolean;
  paddingTop?: number;
  borderTop?: boolean;
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  withShadow?: boolean;
  containerStyle?: object;
  withoutCollapsingFeatureAndStyles?: boolean;
}

// Hook types

export interface UseManageNCTextVirtualizedParams {
  data: string;
  initialCollapsed: boolean;
  withoutCollapsingFeatureAndStyles: boolean;
}
